import React from 'react';

import { useQuery } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import get from 'lodash/get';
import { NumberParam, useQueryParam } from 'use-query-params';

import { NexoyaTeam, NexoyaTeamMember, NexoyaUser } from '../types';

import { InitContext, InitCtx } from '../context/InitProvider';
import { useTeam } from '../context/TeamProvider';
import { INITIAL_QUERY } from '../graphql/initialization/queryInitialData';

import { extractRoleString } from '../utils/string';
import useUserStore from '../store/user';
import { resolveInitialTeam } from './initialTeam';
import history from '../historyUtil';
import { carryGlobalParams } from '../routes/carryGlobalParams';
import { PATHS } from '../routes/paths';

type InitialLoad = {
  error: Error | null | undefined;
  user: NexoyaUser;
};

export default function useInitialQuery(loggedIn: boolean): InitialLoad {
  const { teamId, setTeam } = useTeam();

  const { setUserSuccess } = React.useContext<InitCtx>(InitContext);
  const { setUser, setIsSupportUser } = useUserStore();

  const [urlTeamId, setUrlTeamId] = useQueryParam('team_id', NumberParam);

  // Ref so the init effect doesn't re-fire on internal team changes (e.g. POP-driven restoration from
  // TeamProvider) — it should react only to data loading and URL changes.
  const teamIdRef = React.useRef(teamId);
  React.useEffect(() => {
    teamIdRef.current = teamId;
  }, [teamId]);

  const { data, error } = useQuery(INITIAL_QUERY, {
    variables: {
      teamId,
      lang: 'en_us',
    },
    skip: !loggedIn,
  });

  // Memoize for stable identity: lodash `get` allocates a fresh {}/[] default per call, and the init
  // effect below depends on these refs — without it the effect loops ("Maximum update depth exceeded").
  const user = React.useMemo(() => get(data, 'user', {}), [data]);
  // `?? []` because lodash get only substitutes its default for `undefined`, not `null`, and
  // NexoyaUser.teams is nullable — without this, `teams.length` below throws on a null value.
  const teams = React.useMemo(() => get(data, 'user.teams', []) ?? [], [data]);
  // Comment: disable amplitude logs if nexoya user
  React.useEffect(() => {
    const userRole = teams?.find((t) => t?.team_id === teamId)?.members?.find((m) => m.user_id === user.user_id)
      ?.role?.name;
    if (user?.email && userRole) {
      datadogRum.setUser({
        id: user?.user_id?.toString(),
        name: user?.firstname + ' ' + user?.lastname,
        email: user?.email,
        team: teamId,
        role: extractRoleString(userRole),
      });
    }
  }, [user, teamId, teams]);
  React.useEffect(() => {
    // Bail until the query resolves: while in flight `data` is undefined, and either branch below would
    // complete init against an empty user (flashing the no-teams screen) or churn on unstable refs.
    if (!data) {
      return;
    }
    if (teams.length === 0) {
      // A user with no active teams still needs init to complete — otherwise InitProvider.initializing
      // never flips to false and App renders the loading spinner forever.
      setUserSuccess(user);
      setUser(user);
      setIsSupportUser(false);
      return;
    }

    const requestedTeamId = urlTeamId || teamIdRef.current;

    // The InitialData query has no user.activeRole — it lives on teams[].members[].role — so RBAC needs
    // it materialized here. Keyed on `requestedTeamId` so a ?team_id=X deep-link resolves X's role first.
    const activeRoleName = teams
      ?.find((t: NexoyaTeam) => t?.team_id === requestedTeamId)
      ?.members?.find((m: NexoyaTeamMember) => m.user_id === user.user_id)?.role?.name;
    const userWithActiveRole = activeRoleName
      ? { ...user, activeRole: { __typename: 'RoleDef', name: activeRoleName, description: null } }
      : user;

    setUserSuccess(userWithActiveRole);
    setUser(userWithActiveRole);
    const isSupportUser = activeRoleName?.includes('support');

    setIsSupportUser(isSupportUser);
    const { teamId: teamIdToSet, leavePortfolio } = resolveInitialTeam({
      requestedTeamId,
      explicitTeamId: urlTeamId,
      teamIds: teams.map((t: NexoyaTeam) => t.team_id),
      pathname: history.location.pathname,
    });
    setTeam(teamIdToSet);

    if (leavePortfolio) {
      // The link named someone else's team, so the portfolio in it is not ours either. Writing a
      // substitute team next to that portfolio is what minted the impossible pair.
      history.replace(PATHS.APP.PORTFOLIOS + carryGlobalParams(history.location.search, teamIdToSet));
      return;
    }

    setUrlTeamId(teamIdToSet, 'replaceIn');
  }, [data, teams, user, setUserSuccess, urlTeamId, setTeam, setUrlTeamId]);
  return {
    error,
    user,
  };
}
